import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wrench, Power } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function MaintenanceSection() {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/maintenance")
      .then(({ data }) => { setActive(!!data.active); setMessage(data.message || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (nextActive) => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/maintenance", { active: nextActive, message });
      setActive(!!data.active);
      toast.success(nextActive ? "Mode maintenance activé" : "Mode maintenance désactivé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-foreground/50"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;
  }

  return (
    <div className="max-w-xl" data-testid="admin-maintenance-section">
      <div className="flex items-center gap-2 text-primary mb-2">
        <Wrench size={18} />
        <h2 className="font-display text-2xl">Mode maintenance</h2>
      </div>
      <p className="text-foreground/55 text-sm mb-6">
        Lorsqu'il est actif, les visiteurs voient une page « Nous revenons vite ». Les administrateurs continuent d'accéder au site normalement.
      </p>

      <div className={`rounded-2xl border p-5 mb-5 flex items-center justify-between gap-4 ${active ? "border-amber-400/40 bg-amber-500/10" : "border-white/10 bg-white/[0.02]"}`}>
        <div>
          <p className="font-display text-lg">{active ? "Site en maintenance" : "Site en ligne"}</p>
          <p className="text-foreground/50 text-sm">{active ? "Les visiteurs ne peuvent pas accéder au site." : "Tout le monde peut accéder au site."}</p>
        </div>
        <button
          onClick={() => save(!active)}
          disabled={saving}
          data-testid="admin-maintenance-toggle"
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${active ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 hover:bg-emerald-500/30" : "bg-amber-500/20 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30"}`}
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
          {active ? "Désactiver" : "Activer"}
        </button>
      </div>

      <label className="block text-sm text-foreground/70 mb-2">Message affiché aux visiteurs (optionnel)</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={400}
        rows={3}
        placeholder="Ex : De retour dans quelques heures. Merci de ta patience !"
        data-testid="admin-maintenance-message"
        className="w-full bg-black/30 border border-white/12 rounded-xl px-4 py-3 text-sm text-foreground/90 outline-none focus:border-primary/40 resize-none"
      />
      <button
        onClick={() => save(active)}
        disabled={saving}
        data-testid="admin-maintenance-save-message"
        className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : null} Enregistrer le message
      </button>
    </div>
  );
}
